import React, { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react'
import { isEqual } from 'lodash'
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRow,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonViewDidEnter,
} from '@ionic/react'
import PCRHeader from '../../../components/pcrHeader/PCRHeader.component'
import { checkmarkSharp, chevronBackOutline, pencil } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Chips, { IChip } from '../../../components/chips/Chips.component'
import CommentIcon from '../../../components/commentSection/commentIcon/CommentIcon.component'
import ActionButtons from '../../../components/actionButtons/ActionButtons.component'
import { useDispatch, useSelector } from 'react-redux'
import { showAlert } from '../../../components/alert/store/alert.actions'
import DateTimePicker from '../../../components/calendar/DateTimePicker/DateTimePicker.component'
import LockPortraitScreenComponent from '../../../components/lockPortraitScreen/LockPortraitScreen.component'
import useDeviceMetrics from '../../../utilities/useDeviceMetrics'
import { getDocumentedLocations, LOCATION_ID_MAPPING } from '../../../utilities/bodySurvey.util'
import { deleteAssessment } from '../store/additionalAssessments.actions'
import { BodyAssessmentConstants } from '../bodyAssessment/store/bodyAssessment.constants'
import { IBodySurveyFinding, IExamValue } from '../bodyAssessment/bodySurvey/store/bodySurvey.types'
import {
  deleteBodySurveyExam,
  deleteSummaryData,
  saveBodySurvey,
  setBodValue,
  setDateTime,
  setDocumentFindings,
  setEnabledZoom,
  setExaminationInProgress,
  setFindingsComments,
  setSelectedBodyPartData,
} from '../bodyAssessment/bodySurvey/store/bodySurvey.actions'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { setBodySelectionMode, setSelectedExam } from '../bodyAssessment/store/bodyAssessment.actions'
import { getBodySurveySelector } from '../bodyAssessment/bodySurvey/selectors'
import { IConfigurationsState } from '../../configurations/store/configurations.types'
import { getConfigurationsSelector } from '../../configurations/selectors'
import { compareObjectsIfEqual } from '../../../utilities/object.util'
import {
  initialAdditionalBurnInformation,
  initialBurnsState,
  initialBurnsTbsaState,
} from '../bodyAssessment/burns/store/burns.reducers'
import { initialDrainsAndTubesState } from '../bodyAssessment/drainsTubes/store/drainsAndTubes.reducers'
import { getBurnsSelector } from '../bodyAssessment/burns/selectors'
import { getObstetricsSelector } from '../bodyAssessment/obstetrics/selectors'
import { getDrainsAndTubesSelector } from '../bodyAssessment/drainsTubes/selectors'
import { getPatientDemographicsSelector } from '../../patientDemographics/selectors'
import { initialObstetricsState } from '../bodyAssessment/obstetrics/store/obstetrics.reducers'
import { ITripInformationState } from '../../tripInformation/store/tripInformation.types'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { resetObstetrics, saveObstetrics } from '../bodyAssessment/obstetrics/store/obstetrics.actions'
import { resetDrainsAndTubes } from '../bodyAssessment/drainsTubes/store/drainsAndTubes.actions'
import { resetBurns, resetBurnsTbsa, saveBurns, selectedBurnExam } from '../bodyAssessment/burns/store/burns.actions'
import { IOption } from '../../../common/common.types'
import { goBack } from '../../../utilities/navigation.util'
import './AdditionalAssessmentSummary.component.css'

export interface IChipsList {
  id: number
  name: string
  present: boolean
  color?: string
  segmentId: number
  locationId: number
  customCommandFacId?: number
}
export interface IExam {
  id: number
  examName: string
  bodyPartSummary: IBodySummary[]
  hide?: boolean
}

export interface IBodySummary {
  bodyPartName: string
  examValues: IChipsList[]
  findings: IBodySurveyFinding | null
}

interface IOptionWithKey extends IOption {
  key: string
  initialState?: any
  currentState?: any
  hidden?: boolean
  checked: boolean
}

export type IExamList = Array<IExam>

const AdditionalAssessmentSummary: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const { isMobile, orientation } = useDeviceMetrics()
  const [selectedExam, setSelectedExams] = useState<string>('')
  const [selectedOtherExam, setSelectedOtherExam] = useState<IOptionWithKey | null>()
  const [otherExamData, setOtherExamData] = useState<IOptionWithKey[]>()
  const [isEditable, setIsEditable] = useState(false)

  const configuration: IConfigurationsState = useSelector(getConfigurationsSelector)
  const tripInformationState: ITripInformationState = useSelector(getTripInformationSelector)
  const bodySurvey = useSelector(getBodySurveySelector)
  const drainsAndTubesState = useSelector(getDrainsAndTubesSelector)
  const burnsTbsaState = useSelector(getBurnsSelector)
  const obstetricsState = useSelector(getObstetricsSelector)
  const patientDemographics = useSelector(getPatientDemographicsSelector)

  const { seq, assessDt, examValues, examValuesPNNVs, findings } = bodySurvey
  const { datapointsConfig } = configuration
  const { staffingLevel } = tripInformationState
  const combinedExamOptions = [...examValues, ...examValuesPNNVs]
  const allDocumentedLocationIds = getDocumentedLocations(combinedExamOptions, findings).map((location) => location.id)
  const isGeneralExtremitiesDataPresent = allDocumentedLocationIds.includes(10)

  const { DP_GENERAL_EXTREMITIES, DP_HIDE_FIELDS_BY_STAFF } = datapointsConfig
  const showDrainsAndTubes = staffingLevel?.isAls === 1 || DP_HIDE_FIELDS_BY_STAFF === 0
  const hideGeneralExtremities = DP_GENERAL_EXTREMITIES === 0 && !isGeneralExtremitiesDataPresent

  const burnsTbsaData = {
    burnsExamData: burnsTbsaState.burnsExamData,
    isTBSA: burnsTbsaState.isTBSA,
  }

  const __examValues = [...examValues]
  const __examValuePnnvs = [...examValuesPNNVs]
  const __findings = [...findings]

  const examMap: Record<string, { id: number; examName: string; bodyPartSummary: IBodySummary[]; hide?: boolean }> = {
    [t('additionalAssessments.bodySurvey.bodyParts')]: {
      id: 1,
      examName: t('additionalAssessments.bodySurvey.bodyParts'),
      bodyPartSummary: [],
      hide: false,
    },
    [t('additionalAssessments.bodySurvey.generalExtremities')]: {
      id: 2,
      examName: t('additionalAssessments.bodySurvey.generalExtremities'),
      bodyPartSummary: [],
      hide: hideGeneralExtremities,
    },
    [t('additionalAssessments.bodySurvey.externalSkin')]: {
      id: 3,
      examName: t('additionalAssessments.bodySurvey.externalSkin'),
      bodyPartSummary: [],
      hide: false,
    },
    [t('additionalAssessments.bodySurvey.neuro')]: {
      id: 4,
      examName: t('additionalAssessments.bodySurvey.neuro'),
      bodyPartSummary: [],
      hide: false,
    },
    [t('additionalAssessments.bodySurvey.mental')]: {
      id: 5,
      examName: t('additionalAssessments.bodySurvey.mental'),
      bodyPartSummary: [],
      hide: false,
    },
    [t('additionalAssessments.bodySurvey.heart')]: {
      id: 6,
      examName: t('additionalAssessments.bodySurvey.heart'),
      bodyPartSummary: [],
      hide: false,
    },
    [t('additionalAssessments.bodySurvey.otherExams')]: {
      id: 7,
      examName: t('additionalAssessments.bodySurvey.otherExams'),
      bodyPartSummary: [],
      hide: false,
    },
  }

  const otherExams: IOptionWithKey[] = [
    {
      key: BodyAssessmentConstants.BURNS_RULE_OF_NINES,
      name: t('additionalAssessments.examTypes.BurnsRuleOfNines'),
      initialState: { ...initialBurnsTbsaState, burnsExamData: initialBurnsState.burnsExamData },
      currentState: burnsTbsaData,
      checked: false,
    },
    {
      key: BodyAssessmentConstants.BURNS_TBSA,
      name: t('additionalAssessments.examTypes.BurnsTbsa'),
      initialState: { ...initialBurnsTbsaState, burnsExamData: initialBurnsState.burnsExamData },
      currentState: burnsTbsaData,
      checked: false,
    },
    {
      key: BodyAssessmentConstants.DRAINS_TUBES,
      name: t('additionalAssessments.examTypes.DrainsTubes'),
      hidden: !showDrainsAndTubes,
      initialState: initialDrainsAndTubesState,
      currentState: drainsAndTubesState,
      checked: false,
    },
    {
      key: BodyAssessmentConstants.OBSTETRICS,
      name: t('additionalAssessments.examTypes.Obstetrics'),
      hidden: patientDemographics?.gender?.name === 'Male' ? true : false,
      initialState: initialObstetricsState,
      currentState: obstetricsState,
      checked: false,
    },
  ]

  const transformData = (): IExamList => {
    const examList: IExamList = []

    const filterEnabledExams = Object.fromEntries(Object.entries(examMap).filter(([key, exam]) => !exam.hide))
    const findingsMap: Record<number, { locationId: number; __locationName: string; comments: string }> = {}
    __findings.forEach((finding) => {
      findingsMap[finding.locationId] = {
        locationId: finding.locationId,
        __locationName: finding.__locationName,
        comments: finding.comments || '',
      }
    })

    const addExamValue = (values: IExamValue[], isPresent: boolean, color?: string) => {
      values?.forEach((examValue) => {
        const examName = LOCATION_ID_MAPPING[examValue.locationId] || t('additionalAssessments.bodySurvey.bodyParts')
        let bodyPartName = ''
        if (examName === t('additionalAssessments.bodySurvey.bodyParts')) {
          bodyPartName = examValue.__locationName || findingsMap[examValue.locationId]?.__locationName || ''
        }

        const exam = filterEnabledExams?.[examName]
        let bodyPart = exam?.bodyPartSummary.find((bp: any) => bp.bodyPartName === bodyPartName)

        if (!bodyPart) {
          bodyPart = {
            bodyPartName: examName === t('additionalAssessments.bodySurvey.bodyParts') ? bodyPartName : '',
            examValues: [],
            findings: null,
          }
          exam?.bodyPartSummary?.push(bodyPart)
        }

        bodyPart?.examValues.push({
          id: examValue.customCommandFacId ? examValue.customCommandFacId : examValue.id,
          name: examValue.name,
          segmentId: examValue.segmentId,
          customCommandFacId: examValue.customCommandFacId,
          present: isPresent,
          locationId: examValue.locationId,
          color,
        })
      })
    }

    addExamValue(__examValues, true, 'var(--ion-color-zoll-purple-contrast)')
    addExamValue(__examValuePnnvs, false, 'var(--ion-color-zoll-golden-brown-contrast)')

    Object.values(findingsMap).forEach((finding) => {
      const examName = LOCATION_ID_MAPPING[finding.locationId] || t('additionalAssessments.bodySurvey.bodyParts')
      let bodyPartName = ''
      if (examName === t('additionalAssessments.bodySurvey.bodyParts')) {
        bodyPartName = finding.__locationName
      }

      const exam = filterEnabledExams[examName]
      let bodyPart = exam?.bodyPartSummary?.find((bp: any) => bp.bodyPartName === bodyPartName)

      if (!bodyPart) {
        bodyPart = {
          bodyPartName: examName === t('additionalAssessments.bodySurvey.bodyParts') ? bodyPartName : '',
          examValues: [],
          findings: finding,
        }
        exam?.bodyPartSummary.push(bodyPart)
      } else if (!bodyPart.findings) {
        bodyPart.findings = finding
      }
    })

    for (const key in filterEnabledExams) {
      examList.push(filterEnabledExams[key])
    }

    return examList
  }
  const examList = transformData()

  useEffect(() => {
    if (!isEqual(otherExamData, otherExams) && !selectedExam) {
      setOtherExamData(otherExams)
    }
  }, [otherExams, selectedExam])

  useIonViewDidEnter(() => {
    setOtherExamData(otherExams)
    dispatch(setSelectedBodyPartData([]))
    setIsEditable(false)
  })

  const onBackClick = () => {
    goBack()
  }

  const handleSetSummaryDate = (value: string | null) => {
    dispatch(setDateTime(value))
    dispatch(saveBodySurvey())
    dispatch(setBodValue(true))
  }

  const handleDeleteAssessment = () => {
    dispatch(deleteAssessment(seq))
    history.push(TabConstants.ADDITIONAL_ASSESSMENTS)
  }

  const showDeleteAssessmentConfirmation = () => {
    const alertState = {
      header: t('additionalAssessments.deleteAssessment.header'),
      message: `${t('additionalAssessments.deleteAssessment.message')} ${'Assessment'} ${seq}?`,
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: handleDeleteAssessment }],
    }
    dispatch(showAlert(alertState))
  }

  const handleDeleteExam = (selectedExam: string) => {
    const resetState = () => {
      const resetState = otherExamData?.map((exam) => {
        if (exam.key === selectedOtherExam?.key) {
          return { ...exam, currentState: exam.initialState, checked: false }
        }
        return exam
      })
      setOtherExamData(resetState)
      setSelectedOtherExam(null)
    }

    const resetAndSaveObstetrics = () => {
      dispatch(resetObstetrics())
      dispatch(saveObstetrics())
      resetState()
    }

    const resetAndSaveDrainsTubes = () => {
      dispatch(resetDrainsAndTubes())
      resetState()
    }

    const resetAndSaveBurns = (examType: number) => {
      dispatch(resetBurns())
      dispatch(selectedBurnExam(examType))
      dispatch(saveBurns())
      resetState()
    }

    const examActions: { [key: string]: () => void } = {
      [t('additionalAssessments.examTypes.BurnsRuleOfNines')]: () => resetAndSaveBurns(0),
      [t('additionalAssessments.examTypes.BurnsTbsa')]: () => resetAndSaveBurns(1),
      [t('additionalAssessments.examTypes.DrainsTubes')]: resetAndSaveDrainsTubes,
      [t('additionalAssessments.examTypes.Obstetrics')]: resetAndSaveObstetrics,
    }

    if (selectedExam === t('additionalAssessments.bodySurvey.otherExams')) {
      if (!selectedOtherExam) {
        dispatch(resetObstetrics())
        dispatch(saveObstetrics())
        dispatch(resetDrainsAndTubes())
        dispatch(resetBurns())
        dispatch(saveBurns())
        const resetState = otherExamData?.map((exam) => {
          return { ...exam, currentState: exam.initialState, checked: false }
        })
        setOtherExamData(resetState)
      } else {
        if (selectedOtherExam?.name) {
          const action = examActions[selectedOtherExam?.name]
          if (action) action()
        }
      }
    } else {
      const exam = examList.find((exam) => exam.examName === selectedExam)
      if (exam) {
        const combinedExamValues = exam.bodyPartSummary.flatMap((summary) => summary.examValues)
        const combinedFindingsValues = exam.bodyPartSummary.flatMap((summary) => summary.findings) || []
        dispatch(deleteSummaryData(combinedExamValues, combinedFindingsValues))
      }
    }
  }

  const showDeleteExamConfirmation = () => {
    const examName = selectedOtherExam ? selectedOtherExam.name : selectedExam
    const alertState = {
      header: t('additionalAssessments.deleteExam.header'),
      message:
        t('additionalAssessments.deleteExam.message') +
        examName +
        `${examName === t('additionalAssessments.bodySurvey.otherExams') ? '' : ' exam'}` +
        ' ?',
      buttons: [{ text: 'Cancel' }, { text: 'Ok', role: 'destructive', handler: () => handleDeleteExam(selectedExam) }],
    }
    dispatch(showAlert(alertState))
  }

  const madeFindingsOnExam = (exam: IOptionWithKey | null) => {
    if (!exam) return false
    const key = exam.key
    const additionalBurninformationData: any = burnsTbsaState.additionalBurnInformation
    switch (key) {
      case BodyAssessmentConstants.BURNS_TBSA:
        return (
          burnsTbsaState.isTBSA === 1 &&
          (burnsTbsaState.burnsExamData.length !== 0 ||
            !compareObjectsIfEqual(initialAdditionalBurnInformation, additionalBurninformationData))
        )
      case BodyAssessmentConstants.BURNS_RULE_OF_NINES:
        return (
          burnsTbsaState.isTBSA === 0 &&
          (burnsTbsaState.burnsExamData.length !== 0 ||
            !compareObjectsIfEqual(initialAdditionalBurnInformation, additionalBurninformationData))
        )
      default:
        if (exam.initialState && exam.currentState) {
          return !compareObjectsIfEqual(exam.initialState, exam.currentState)
        }
        return true
    }
  }

  const hasExamData = (examName: string): boolean => {
    const data = examList.find((exam) => exam.examName === examName)
    const hasDataInAnyExam = otherExamData?.some((exam) => madeFindingsOnExam(exam))
    const allExamsSelected = otherExamData?.every((exam) => exam?.checked)
    return (
      !!data?.bodyPartSummary?.length ||
      (allExamsSelected && hasDataInAnyExam) ||
      madeFindingsOnExam(selectedOtherExam ?? null)
    )
  }

  const editableScreenButtons = [
    {
      label: t('common.buttons.Done'),
      className: 'btn btn-secondary mr-8',
      onClick: () => setIsEditable(false),
    },
    {
      label: t('common.buttons.Delete'),
      className: 'btn',
      fill: 'outline',
      color: 'danger',
      disabled: !hasExamData(selectedExam),
      onClick: showDeleteExamConfirmation,
    },
  ]

  const nonEditableScreenButtons = [
    {
      label: t('common.buttons.Delete'),
      className: 'btn mr-8',
      fill: 'outline',
      color: 'danger',
      onClick: showDeleteAssessmentConfirmation,
    },
    {
      label: t('common.buttons.Edit'),
      className: 'btn btn-primary',
      onClick: () => setIsEditable((prev) => !prev),
    },
  ]

  const examTypeOnClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!isEditable && selectedExam !== t('additionalAssessments.bodySurvey.otherExams')) return
    const currentValue = selectedExam === e.target.value ? '' : e.target.value
    const isOtherExam = currentValue === t('additionalAssessments.bodySurvey.otherExams')
    const hasDataInAnyExam = otherExamData?.some((exam) => madeFindingsOnExam(exam))
    if (hasDataInAnyExam && isOtherExam) {
      const setOtherExamsTrue = otherExamData?.map((exam) => ({
        ...exam,
        checked: isEditable ? true : false,
      }))
      setOtherExamData(setOtherExamsTrue)
    }
    setSelectedExams(currentValue)
    setSelectedOtherExam(null)
  }

  const otherExamTypeOnClick = (e: BaseSyntheticEvent, exam: IOptionWithKey) => {
    e.preventDefault()
    const examValue = exam.name === selectedOtherExam?.name ? null : exam
    const setOtherExamsTrue = otherExamData?.map((otherExam) => {
      return {
        ...otherExam,
        checked: otherExam.name === exam.name ? !otherExam.checked : false,
      }
    })
    setOtherExamData(setOtherExamsTrue)
    if (examValue) {
      setSelectedExams(t('additionalAssessments.bodySurvey.otherExams'))
    } else {
      setSelectedExams('')
    }
    setSelectedOtherExam(examValue)
  }

  const handleDeleteBurns = (currentExam: string) => {
    const updatedTBSA = burnsTbsaState.isTBSA === 1 ? 0 : 1
    dispatch(resetBurnsTbsa())
    setTimeout(() => {
      dispatch(selectedBurnExam(updatedTBSA))
      setSelectedExamData(currentExam)
      dispatch(setDocumentFindings(false))
      dispatch(setExaminationInProgress(false))
      history.push(TabConstants.BODY_ASSESSMENT)
    }, 100)
  }

  const setSelectedExamData = (examName: string) => {
    if (examName === t('additionalAssessments.bodySurvey.bodyParts')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BODY_SURVEY))
      dispatch(setBodySelectionMode({ id: 1, name: 'Body parts' }))
    }
    if (examName === t('additionalAssessments.bodySurvey.externalSkin')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BODY_SURVEY))
      dispatch(setBodySelectionMode({ id: 2, name: 'External / Skin' }))
    }
    if (examName === t('additionalAssessments.bodySurvey.generalExtremities')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BODY_SURVEY))
      dispatch(setBodySelectionMode({ id: 3, name: 'General Extremities' }))
    }
    if (examName === t('additionalAssessments.bodySurvey.neuro')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.NEURO_CONDITIONS))
    }
    if (examName === t('additionalAssessments.bodySurvey.mental')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.MENTAL_CONDITIONS))
    }
    if (examName === t('additionalAssessments.bodySurvey.heart')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.CARDIO_CONDITIONS))
    }
    if (examName === t('additionalAssessments.examTypes.Obstetrics')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.OBSTETRICS))
    }
    if (examName === t('additionalAssessments.examTypes.DrainsTubes')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.DRAINS_TUBES))
    }
    if (examName === t('additionalAssessments.examTypes.BurnsTbsa')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BURNS_TBSA))
    }
    if (examName === t('additionalAssessments.examTypes.BurnsRuleOfNines')) {
      dispatch(setSelectedExam(BodyAssessmentConstants.BURNS_RULE_OF_NINES))
    }
  }

  const handleExamEditBtnClick = (examName: string) => {
    const isTraumaExam = examName === t('additionalAssessments.bodySurvey.bodyParts')
    if (isTraumaExam) {
      dispatch(setEnabledZoom(false))
    }
    if (
      examName === t('additionalAssessments.examTypes.BurnsRuleOfNines') ||
      examName === t('additionalAssessments.examTypes.BurnsTbsa')
    ) {
      let currentExam = ''

      if (examName === t('additionalAssessments.examTypes.BurnsRuleOfNines')) {
        currentExam = BodyAssessmentConstants.BURNS_RULE_OF_NINES
      }
      if (examName === t('additionalAssessments.examTypes.BurnsTbsa')) {
        currentExam = BodyAssessmentConstants.BURNS_TBSA
      }

      let currentSelectedSection = ''
      if (burnsTbsaState.isTBSA === 1 && currentExam === BodyAssessmentConstants.BURNS_RULE_OF_NINES) {
        currentSelectedSection = t('additionalAssessments.examTypes.BurnsRuleOfNines')
      } else if (burnsTbsaState.isTBSA === 0 && currentExam === BodyAssessmentConstants.BURNS_TBSA) {
        currentSelectedSection = t('additionalAssessments.examTypes.BurnsTbsa')
      }

      if (currentSelectedSection.length) {
        const alertState = {
          header: currentSelectedSection,
          message: t('burns.deleteBurns.message'),
          buttons: [
            { text: 'Cancel' },
            { text: 'Ok', role: 'destructive', handler: () => handleDeleteBurns(examName) },
          ],
        }
        dispatch(showAlert(alertState))
        return
      }
    }
    setSelectedExamData(examName)
    dispatch(setDocumentFindings(false))
    dispatch(setExaminationInProgress(false))
    history.push(TabConstants.BODY_ASSESSMENT)
  }

  const handleComments = (e: CustomEvent, finding: IBodySurveyFinding | null, examName: string) => {
    if (e.detail.value === finding?.comments) return
    e.preventDefault()
    if (examName === t('additionalAssessments.bodySurvey.bodyParts')) {
      dispatch(setSelectedBodyPartData([{ id: finding?.locationId, name: finding?.__locationName }]))
    }
    setSelectedExamData(examName)
    dispatch(setFindingsComments(e.detail.value))
  }

  useEffect(() => {
    if (isEditable === false) {
      const setOtherExamsFalse = otherExams?.map((otherExam) => {
        return {
          ...otherExam,
          checked: false,
        }
      })
      setOtherExamData(setOtherExamsFalse)
      setSelectedExams('')
      setSelectedOtherExam(null)
    }
  }, [isEditable])

  const dateTimePickerLabel = !assessDt && { label: 'MM/DD/YYYY mm:hh' }
  const allExamsSelected = otherExamData?.every((exam) => exam?.checked)

  const otherExamContent = useMemo(() => {
    return (
      otherExamData &&
      otherExamData.map(
        (exams, key) =>
          !exams.hidden && (
            <IonRow key={key}>
              <IonCol className='ion-no-padding'>
                <IonItem className='pt-0 summary-exam-type-row summary-exam-type-other-row'>
                  <IonLabel className='m-0' color='medium'>
                    {exams?.name}
                    {madeFindingsOnExam(exams) && (
                      <IonIcon color='success' className='checkmark-icon ml-10' icon={checkmarkSharp} />
                    )}
                  </IonLabel>
                  {isEditable && (
                    <>
                      <IonCheckbox
                        onClick={(e) => otherExamTypeOnClick(e, exams)}
                        slot='start'
                        value={exams?.name}
                        data-label={exams?.name}
                        data-testid={exams?.name}
                        checked={exams.checked}
                        disabled={allExamsSelected}
                      />
                      <IonIcon
                        icon={pencil}
                        color='neutral-base'
                        onClick={() => handleExamEditBtnClick(exams.name ?? '')}
                        className='cursor-pointer'
                      />
                    </>
                  )}
                </IonItem>
              </IonCol>
            </IonRow>
          )
      )
    )
  }, [
    otherExamData,
    isEditable,
    otherExamTypeOnClick,
    handleDeleteExam,
    handleExamEditBtnClick,
    madeFindingsOnExam,
    allExamsSelected,
  ])

  const examContent = (examData: any) => {
    const { id, examName, bodyPartSummary } = examData

    return (
      <IonRow key={id}>
        <IonCol className='ion-no-padding'>
          <IonItem className='summary-exam-type-row'>
            <IonLabel className='m-0'>{`${id} - ${examName}`}</IonLabel>
            {isEditable && (
              <>
                <IonRadio
                  onClick={(e) => examTypeOnClick(e)}
                  slot='start'
                  value={examName}
                  data-label={examName}
                  data-testid={examName}
                />
                {id !== 7 && (
                  <IonIcon
                    icon={pencil}
                    color='neutral-base'
                    onClick={() => handleExamEditBtnClick(examName)}
                    className='cursor-pointer'
                  />
                )}
              </>
            )}
          </IonItem>

          <div className='summary-content-block ion-padding'>
            {bodyPartSummary.map((bodyPart: IBodySummary, index: number) => (
              <div key={index}>
                {bodyPart.bodyPartName && <div className='fw-500 font-size-16 mb-10'>{bodyPart.bodyPartName}</div>}
                {bodyPart.examValues.length > 0 && (
                  <div className='mb-10'>
                    <Chips
                      chipsArray={bodyPart.examValues}
                      handleChipDeletion={(option: IChip) => dispatch(deleteBodySurveyExam(option))}
                      showCrossBtn={isEditable}
                    />
                  </div>
                )}
                {bodyPart?.findings && (
                  <IonRow className='ion-align-items-start'>
                    {!isEditable && (
                      <IonCol size='auto'>
                        <CommentIcon commentText='placeholder' enableClick={false} />
                      </IonCol>
                    )}
                    <IonCol className='font-size-15 fw-300'>
                      <IonTextarea
                        debounce={350}
                        className={`summary-exam-comments ${isEditable ? 'active' : ''}`}
                        data-testid={`${examName}Comments`}
                        value={bodyPart?.findings?.comments}
                        onIonChange={(e) => handleComments(e, bodyPart?.findings, examName)}
                        maxlength={1000}
                        autocapitalize='sentences'
                        autoGrow={true}
                        readonly={!isEditable}
                      />
                    </IonCol>
                  </IonRow>
                )}
              </div>
            ))}
          </div>
        </IonCol>
      </IonRow>
    )
  }

  return (
    <IonPage id='additional-assessment-summary-page'>
      <PCRHeader />
      <IonHeader className='ion-no-padding'>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton color='primary-alt' data-testid='BackButton' onClick={onBackClick}>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {t('additionalAssessments.Assessment')} {seq}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      {isMobile && orientation === 'landscape' ? (
        <LockPortraitScreenComponent />
      ) : (
        <>
          <IonContent className='summary-container'>
            <IonRow className='ion-padding summary-date-time-row'>
              <IonCol className='ion-no-padding'>
                <h4>{t('additionalAssessments.Summary')}</h4>
              </IonCol>
              <IonCol size='auto' className='ion-no-padding'>
                <DateTimePicker
                  className='summary-date-time-picker'
                  dataTestID='summaryDateTimePicker'
                  selectedDateValue={assessDt}
                  action={handleSetSummaryDate}
                  disabled={false}
                  hideClearBtn={true}
                  {...dateTimePickerLabel}
                />
              </IonCol>
            </IonRow>
            <IonList>
              <IonRadioGroup value={selectedExam} allowEmptySelection={true}>
                {examList?.map((examData) => examContent(examData))}
              </IonRadioGroup>
              {otherExamContent}
            </IonList>
          </IonContent>
          <IonFooter>
            <IonToolbar color='light' className='p-10 '>
              <ActionButtons actionButtonObj={isEditable ? editableScreenButtons : nonEditableScreenButtons} />
            </IonToolbar>
          </IonFooter>
        </>
      )}
    </IonPage>
  )
}

export default AdditionalAssessmentSummary
